// src/components/Sidebar/Sidebar.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Ana global drawer sidebar.
// Masaüstünde YOKTUR — sadece mobil/logo tıklamasıyla açılır overlay drawer.
// Rotalar: /bonebeat · /boneframe · /boneforge · /settings · /admin
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSidebar } from "../../context/SidebarContext.jsx";
import { useAuth }    from "../../context/AuthContext.jsx";
import { useAdmin }   from "../../context/AdminContext.jsx";
import { logout }     from "../../firebase/auth.js";

const C = {
  bg:         "#100d07",
  border:     "rgba(212,168,83,0.4)",
  borderHov:  "rgba(212,168,83,0.6)",
  gold:       "var(--bone-text-accent)",
  goldDim:    "var(--bone-text-accent)",
  ivory:      "var(--bone-text-primary)",
  ivoryDim:   "var(--bone-text-secondary)",
  ivoryGhost: "var(--bone-text-muted)",
};

// ── Korumalı rotalar (giriş gerekenler) ────────────────────────────────────
const PROTECTED = ["/bonebeat", "/boneframe", "/boneforge", "/settings", "/admin"];

function GoldRule({ opacity = 0.12 }) {
  return (
    <div style={{
      height: 1, margin: "0 20px",
      background: `linear-gradient(90deg,transparent,rgba(212,168,83,${opacity}),transparent)`,
    }} />
  );
}

function NavItem({ path, label, sub, icon, user, onClose }) {
  const [hov, setHov] = useState(false);
  const navigate      = useNavigate();
  const location      = useLocation();
  const active        = location.pathname === path;
  const lit           = active || hov;

  function handleClick(e) {
    if (PROTECTED.includes(path) && !user) {
      e.preventDefault();
      onClose();
      navigate("/auth");
    } else {
      onClose();
    }
  }

  return (
    <Link
      to={path}
      onClick={handleClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", alignItems: "center", gap: 14,
        padding: "12px 20px", textDecoration: "none", position: "relative",
        background: active ? "rgba(212,168,83,0.07)" : hov ? "rgba(212,168,83,0.04)" : "transparent",
        borderLeft: `2px solid ${active ? C.gold : "transparent"}`,
        transition: "all 0.3s ease",
      }}
    >
      {/* Hover parlaklığı */}
      <span style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "linear-gradient(90deg,rgba(212,168,83,0.05),transparent)",
        opacity: lit ? 1 : 0, transition: "opacity 0.3s ease",
      }} />

      {/* Dikey çizgi */}
      <span style={{
        width: 1, height: 28, flexShrink: 0,
        background: `rgba(212,168,83,${lit ? 0.3 : 0.1})`,
        transition: "background 0.3s ease",
      }} />

      {/* İkon */}
      <span style={{
        color: lit ? C.goldDim : "rgba(212,168,83,0.22)",
        display: "flex", flexShrink: 0, transition: "color 0.3s ease",
      }}>{icon}</span>

      {/* Etiket */}
      <span style={{ flex: 1 }}>
        <span style={{
          display: "block", fontFamily: "'Courier Prime',monospace",
          fontSize: 12, letterSpacing: "0.12em", marginBottom: 2,
          color: active ? C.ivory : hov ? C.ivoryDim : C.ivoryGhost,
          transition: "color 0.3s ease",
        }}>{label}</span>
        <span style={{
          display: "block", fontFamily: "'Courier Prime',monospace",
          fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase",
          color: lit ? "var(--bone-text-accent)" : "var(--bone-text-muted)",
          transition: "color 0.3s ease",
        }}>{sub}</span>
      </span>

      {/* Kilit ikonu — giriş gerektiren ve kullanıcı yok */}
      {PROTECTED.includes(path) && !user && (
        <span style={{ fontSize: 10, color: "var(--bone-text-muted)", flexShrink: 0 }}>🔒</span>
      )}

      {/* Aktif nokta */}
      {active && (
        <span style={{
          width: 4, height: 4, borderRadius: "50%", flexShrink: 0,
          background: C.gold, boxShadow: `0 0 8px ${C.gold}`,
        }} />
      )}
    </Link>
  );
}

function ProfileStrip({ user, userData }) {
  const initial = (user.displayName || user.email || "?")[0].toUpperCase();
  return (
    <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{
        width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
        border: `1px solid ${C.borderHov}`, background: "rgba(212,168,83,0.08)",
        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
      }}>
        {userData?.photoURL
          ? <img src={userData.photoURL} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 12, fontWeight: 700, color: C.goldDim }}>{initial}</span>
        }
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.ivoryDim,
          letterSpacing: "0.08em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{user.displayName || "Gezgin"}</div>
        <div style={{
          fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "rgba(212,168,83,0.3)",
          letterSpacing: "0.1em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{user.email}</div>
      </div>
    </div>
  );
}

export default function Sidebar() {
  const { isOpen, closeSidebar } = useSidebar();
  const { user, userData }       = useAuth();
  const { isAdmin, loading }     = useAdmin();
  const location                 = useLocation();
  const { t }                    = useTranslation();
  const navigate                 = useNavigate();

  // Escape ile kapat
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeSidebar(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeSidebar]);

  // Rota değişince kapat
  useEffect(() => {
    closeSidebar();
  }, [location.pathname, closeSidebar]);

  const handleLogout = async () => {
    try { await logout(); } catch { /* sessiz */ }
    closeSidebar();
  };

  return (
    <>
      <style>{`
        @keyframes bc-navReveal {
          from { transform: translateX(-16px); opacity: 0; }
          to   { transform: translateX(0);     opacity: 1; }
        }
        @keyframes bc-flickerIn {
          0%   { opacity: 0; }
          60%  { opacity: 0.8; }
          75%  { opacity: 0.4; }
          100% { opacity: 1; }
        }
        .bc-overlay {
          position: fixed; inset: 0; z-index: 90;
          background: rgba(5,4,1,0.65);
          backdrop-filter: blur(3px);
          -webkit-backdrop-filter: blur(3px);
          transition: opacity 0.35s ease;
        }
        .bc-overlay.open   { opacity: 1; pointer-events: auto; }
        .bc-overlay.closed { opacity: 0; pointer-events: none; }
        .bc-drawer {
          position: fixed; top: 0; left: 0;
          height: 100%; width: 300px; z-index: 95;
          will-change: transform;
          transition: transform 0.38s cubic-bezier(0.16,1,0.3,1);
          background: ${C.bg};
          border-right: 1px solid rgba(212,168,83,0.1);
          display: flex; flex-direction: column;
          overflow: hidden;
        }
        .bc-drawer.open   { transform: translateX(0); }
        .bc-drawer.closed { transform: translateX(-100%); }
        .bc-nav-item {
          opacity: 0;
          animation: bc-navReveal 0.45s cubic-bezier(0.16,1,0.3,1) forwards;
        }
        .bc-nav-item a { display: flex; }
      `}</style>

      {/* Overlay */}
      <div
        className={`bc-overlay ${isOpen ? "open" : "closed"}`}
        onClick={closeSidebar}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside className={`bc-drawer ${isOpen ? "open" : "closed"}`} role="dialog" aria-modal="true" aria-label="Ana Menü">
        <div style={{ display: "flex", flexDirection: "column", height: "100%", overflowY: "auto" }}>

          {/* ── Başlık ── */}
          <div style={{
            padding: "20px 20px 16px",
            borderBottom: "1px solid rgba(212,168,83,0.1)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexShrink: 0,
          }}>
            <div>
              <div style={{
                fontFamily: "'Playfair Display',serif", fontWeight: 700,
                fontStyle: "italic", fontSize: 18, color: C.goldDim,
                letterSpacing: "0.06em",
                textShadow: "0 0 24px rgba(212,168,83,0.25)",
                animation: isOpen ? "bc-flickerIn 0.6s ease 0.1s both" : "none",
              }}>BoneCast</div>
              <div style={{
                fontFamily: "'Courier Prime',monospace", fontSize: 8,
                letterSpacing: "0.3em", textTransform: "uppercase",
                color: "var(--bone-text-muted)", marginTop: 3,
              }}>Arşiv · v1.0.4</div>
            </div>

            {/* Kapat butonu — her zaman görünür (bu global drawer, mobil + masaüstü) */}
            <button
              onClick={closeSidebar}
              aria-label="Menüyü kapat"
              style={{
                width: 32, height: 32,
                borderRadius: 8,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "transparent",
                border: "1px solid rgba(212,168,83,0.4)",
                cursor: "pointer",
                color: "var(--bone-text-secondary)",
                transition: "all 0.2s ease",
                flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(212,168,83,0.65)"; e.currentTarget.style.color = C.goldDim; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(212,168,83,0.4)"; e.currentTarget.style.color = "var(--bone-text-secondary)"; }}
            >
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* ── Navigasyon ── */}
          <nav style={{ flex: 1, paddingBottom: 8, overflowY: "auto" }}>

            {/* ✦ MODÜLLER ✦ */}
            <div style={{ padding: "14px 20px 6px" }}>
              <span style={{
                fontFamily: "'Courier Prime',monospace", fontSize: 9,
                color: "var(--bone-text-muted)", textTransform: "uppercase", letterSpacing: "0.22em",
              }}>✦ {t('sidebar.modules')} ✦</span>
            </div>

            {/* I — BoneBeat */}
            <div className="bc-nav-item" style={{ animationDelay: "0.08s" }}>
              <NavItem
                path="/bonebeat" label="BoneBeat" sub={t('home.modules.beat.subtitle')}
                user={user} onClose={closeSidebar}
                icon={
                  <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}>
                    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                  </svg>
                }
              />
            </div>

            {/* II — BoneFrame */}
            <div className="bc-nav-item" style={{ animationDelay: "0.16s" }}>
              <NavItem
                path="/boneframe" label="BoneFrame" sub="Cinema Library"
                user={user} onClose={closeSidebar}
                icon={
                  <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}>
                    <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                  </svg>
                }
              />
            </div>

            {/* III — BoneForge */}
            <div className="bc-nav-item" style={{ animationDelay: "0.24s" }}>
              <NavItem
                path="/boneforge" label="BoneForge" sub="The Forge · Smithy"
                user={user} onClose={closeSidebar}
                icon={
                  <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}>
                    <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z" />
                  </svg>
                }
              />
            </div>

            {/* ✦ HESABIM ✦ — sadece giriş yapmış kullanıcı */}
            {user && (
              <React.Fragment>
                <GoldRule opacity={0.08} />
                <div style={{ padding: "10px 20px 4px" }}>
                  <span style={{
                    fontFamily: "'Courier Prime',monospace", fontSize: 9,
                    color: "var(--bone-text-muted)", textTransform: "uppercase", letterSpacing: "0.2em",
                  }}>✦ {t('sidebar.account')} ✦</span>
                </div>

                {/* Admin Paneli — loading bitene kadar render etme */}
                {!loading && isAdmin && (
                  <div className="bc-nav-item" style={{ animationDelay: "0.29s" }}>
                    <NavItem
                      path="/admin" label={t('sidebar.adminPanel')} sub={t('sidebar.adminSub')}
                      user={user} onClose={closeSidebar}
                      icon={
                        <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}>
                          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 4l5 2.18V11c0 3.5-2.33 6.79-5 7.93-2.67-1.14-5-4.43-5-7.93V7.18L12 5z" />
                        </svg>
                      }
                    />
                  </div>
                )}

                {/* Hesap Ayarları */}
                <div className="bc-nav-item" style={{ animationDelay: "0.36s" }}>
                  <NavItem
                    path="/settings" label={t('settings.title')} sub={t('sidebar.settingsSub')}
                    user={user} onClose={closeSidebar}
                    icon={
                      <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}>
                        <path d="M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96a7 7 0 0 0-1.62-.94l-.36-2.54A.48.48 0 0 0 14 2h-4a.48.48 0 0 0-.48.41l-.36 2.54a7.4 7.4 0 0 0-1.62.94l-2.39-.96a.48.48 0 0 0-.59.22L2.74 8.87a.48.48 0 0 0 .12.6l2.03 1.58a7.3 7.3 0 0 0-.07.95c0 .31.02.63.07.93l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.36 1.04.67 1.62.94l.36 2.54c.05.24.27.41.51.41h4c.24 0 .46-.17.5-.41l.36-2.54a7.4 7.4 0 0 0 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.47.47 0 0 0-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z" />
                      </svg>
                    }
                  />
                </div>
              </React.Fragment>
            )}

            {/* ✦ DESTEK ✦ */}
            <GoldRule opacity={0.06} />
            <div style={{ padding: "10px 20px 4px" }}>
              <span style={{
                fontFamily: "'Courier Prime',monospace", fontSize: 9,
                color: "var(--bone-text-muted)", textTransform: "uppercase", letterSpacing: "0.2em",
              }}>✦ {t('sidebar.support')} ✦</span>
            </div>
            <button
              onClick={() => { closeSidebar(); navigate("/donate"); }}
              onMouseEnter={e => {
                e.currentTarget.style.background = "rgba(212,168,83,0.04)";
                e.currentTarget.style.borderLeftColor = "rgba(212,168,83,0.5)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderLeftColor = "transparent";
              }}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 14,
                padding: "12px 20px", background: "transparent",
                border: "none", borderLeft: "2px solid transparent",
                cursor: "pointer", transition: "all 0.25s ease",
              }}
            >
              <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 10, fontStyle: "italic", color: "var(--bone-text-accent)", width: 20, textAlign: "right" }}>♥</span>
              <span style={{ width: 1, height: 28, background: "rgba(212,168,83,0.15)", flexShrink: 0 }} />
              <span style={{ flex: 1, textAlign: "left" }}>
                <span style={{ display: "block", fontFamily: "'Courier Prime',monospace", fontSize: 12, letterSpacing: "0.12em", color: "var(--bone-text-primary)", marginBottom: 2 }}>
                  {t('sidebar.donate')}
                </span>
                <span style={{ display: "block", fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--bone-text-muted)" }}>
                  {t('sidebar.donateSub')}
                </span>
              </span>
            </button>

          </nav>

          {/* Profil şeridi */}
          {user && (
            <>
              <GoldRule opacity={0.1} />
              <ProfileStrip user={user} userData={userData} />
            </>
          )}

          {/* Alt bilgi + Çıkış */}
          <div style={{
            borderTop: "1px solid rgba(212,168,83,0.4)", flexShrink: 0,
            padding: "14px 20px", display: "flex", flexDirection: "column", gap: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 7 }}>
                <a href="https://www.instagram.com/bonecastofficial?igsh=MWJvYWdkc3E2bWE4bg=="
                  target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, border: "1px solid rgba(212,168,83,0.4)", borderRadius: 6, color: "var(--bone-text-accent)", textDecoration: "none" }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                  </svg>
                </a>
                <a href="https://x.com/BoneCastOfcl" target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, border: "1px solid rgba(212,168,83,0.4)", borderRadius: 6, color: "var(--bone-text-accent)", textDecoration: "none" }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
              </div>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "var(--bone-text-muted)", letterSpacing: "0.15em" }}>
                v1.0.4 · 2026
              </span>
            </div>

            {user && (
              <button onClick={handleLogout}
                style={{
                  width: "100%", background: "transparent",
                  border: "1px solid rgba(212,168,83,0.4)",
                  borderRadius: 8,
                  color: "var(--bone-text-accent)", padding: "9px 0",
                  fontFamily: "'Courier Prime',monospace", fontSize: 10,
                  letterSpacing: "0.22em", textTransform: "uppercase",
                  cursor: "pointer", transition: "all 0.2s ease",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(200,80,80,0.4)"; e.currentTarget.style.color = "rgba(230,100,100,0.8)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(212,168,83,0.4)"; e.currentTarget.style.color = "var(--bone-text-accent)"; }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width={11} height={11}>
                  <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                </svg>
                {t('auth.logout')}
              </button>
            )}

            <div style={{
              fontFamily: "'Courier Prime',monospace", fontSize: 8,
              color: "var(--bone-text-muted)", letterSpacing: "0.12em",
              lineHeight: 1.6, textAlign: "center",
            }}>
              {t('sidebar.copyright')}
            </div>
          </div>

        </div>
      </aside>
    </>
  );
}
